import { useMemo } from "react";
import type { FliptClient } from "@flipt-io/flipt-client-js";
import { useFliptSelector } from "@flipt-io/flipt-client-react";
import useUser from "~/contexts/user";
import { FLIPT_ROUTE_KEYS, FliptFlagKey } from "~/router/constants";

export interface ManifoldNextGenFeature {
  enabled: boolean;
  isLoading: boolean;
}

/**
 * Shared boolean-flag evaluation for the `manifold-nextgen` and
 * `force-manifold-nextgen` flags. Exposes `isLoading` while Flipt initializes
 * so callers do not flash a false `enabled` and act on it prematurely.
 */
const useFliptFeature = (flagKey: FliptFlagKey): ManifoldNextGenFeature => {
  const user = useUser();

  const isFliptPending = useFliptSelector(
    (client: FliptClient | null, isLoading: boolean, _error: Error | null) => !client || isLoading
  );

  const enabled = useFliptSelector(
    (client: FliptClient | null, isLoading: boolean, error: Error | null) => {
      if (!client || isLoading) {
        return false;
      }
      if (error) {
        return false;
      }
      try {
        return client.evaluateBoolean({
          flagKey,
          entityId: user?.email ?? "",
          context: {},
        }).enabled;
      } catch (e) {
        console.error(`Error evaluating boolean flag ${flagKey}:`, e);
        return false;
      }
    }
  );

  return useMemo(() => ({ isLoading: isFliptPending, enabled }), [isFliptPending, enabled]);
};

/**
 * Flipt `manifold-nextgen`, read at the router to gate `/nextgen` routes.
 */
export const useManifoldNextGenFeature = (): ManifoldNextGenFeature =>
  useFliptFeature(FLIPT_ROUTE_KEYS.MANIFOLD_NEXTGEN);

/**
 * Flipt `force-manifold-nextgen`, targeted at a Flipt segment (e.g. trial
 * users). When enabled, the user is locked into the new experience: the
 * opt-in prompt is skipped and "Switch to classic look" is hidden.
 */
export const useForceManifoldNextGenFeature = (): ManifoldNextGenFeature =>
  useFliptFeature(FLIPT_ROUTE_KEYS.FORCE_MANIFOLD_NEXTGEN);
